/**
 * `persistentHighlightData.ts` — web port of Unity
 * `Models/Data/PersistentHighlightData.cs`, together with the visual-state math
 * from `Models/PersistentEntityHighlightModelBase.cs` and the desktop tuning
 * constants from `CampfireScenes/.../PersistentEntityHighlightDesktopModel.cs`.
 *
 * The persisted payload is a Unity-JsonUtility string stored VERBATIM in the
 * entity snapshot row's `PersistentHighlightJson` (S) attribute (and mirrored on
 * the `update_entitysnapshot` wire as the snapshot's `persistentHighlightJson`
 * field):
 *
 *   {"enabled":true,"semantic":"hazard","colorHex":"","pulseMode":"continuous",
 *    "pulseCount":-1,"thicknessMode":"thick"}
 *
 * Contract facts kept verbatim (`PersistentHighlightData.cs`):
 *   - `Serialize` returns null (attribute omitted / cleared) when the highlight
 *     is disabled — "no highlight" is the ABSENCE of the payload.
 *   - `Deserialize` returns null for blank/invalid/disabled JSON and normalizes
 *     every field through the alias tables below.
 *   - `pulseCount` is persisted as the RAW int: `-1` means infinite, and any
 *     value `<= 0` normalizes to `-1`.
 *   - `colorHex` is persisted normalized to `#RRGGBB` (uppercase, alpha
 *     dropped), and JsonUtility writes a null string as `""`.
 *
 * Colors are DISPLAY values, not light-linear ones: Unity stores its `Color32`
 * semantics in gamma space and shows them as-is, so `resolveHighlightColor`
 * returns raw 0..1 components meant for `Color.setRGB` with no conversion.
 * See `runtime/engine/COLOR_PIPELINE.md` — the outline colors are Population B.
 */

export const PersistentHighlightSemantics = {
  Attention: "attention",
  Correct: "correct",
  Hazard: "hazard",
} as const;

export const PersistentHighlightPulseModes = {
  SingleSoft: "singleSoft",
  Continuous: "continuous",
  None: "none",
} as const;

export const PersistentHighlightThicknessModes = {
  Thin: "thin",
  Medium: "medium",
  Thick: "thick",
} as const;

export type PersistentHighlightSemantic =
  (typeof PersistentHighlightSemantics)[keyof typeof PersistentHighlightSemantics];
export type PersistentHighlightPulseMode =
  (typeof PersistentHighlightPulseModes)[keyof typeof PersistentHighlightPulseModes];
export type PersistentHighlightThicknessMode =
  (typeof PersistentHighlightThicknessModes)[keyof typeof PersistentHighlightThicknessModes];

export interface PersistentHighlightData {
  readonly enabled: boolean;
  readonly semantic: PersistentHighlightSemantic;
  /** Normalized `#RRGGBB`, or `null` when the semantic color is used. */
  readonly colorHex: string | null;
  readonly pulseMode: PersistentHighlightPulseMode;
  /** Positive cycle count, or `null` for infinite (persisted as `-1`). */
  readonly pulseCount: number | null;
  readonly thicknessMode: PersistentHighlightThicknessMode;
}

export type Rgb = readonly [number, number, number];

/** Unity `PersistentEntityHighlightModelBase.k_continuousPulseCyclesPerSecond`. */
export const HIGHLIGHT_PULSE_CYCLES_PER_SECOND = 0.8;
/** Unity `PersistentEntityHighlightModelBase.k_singleSoftPulseDuration`. */
export const HIGHLIGHT_ENTRY_PULSE_DURATION_SECONDS = 1.1;

// Desktop tuning (`PersistentEntityHighlightDesktopModel` overrides; the two
// unoverridden peaks come from the shared base class).
// Unity computes a peak INTENSITY too (base `GetPeakIntensity` = 1) and then
// never reads it — only the thickness pulses. Not carried here.
const K_STEADY_INTENSITY = 0.22;
const K_STEADY_THICKNESS = 0.15;
const K_PEAK_THICKNESS = 0.62;

/** Unity `PersistentHighlightData` default instance. */
export const DEFAULT_PERSISTENT_HIGHLIGHT: PersistentHighlightData = {
  enabled: true,
  semantic: PersistentHighlightSemantics.Attention,
  colorHex: null,
  pulseMode: PersistentHighlightPulseModes.Continuous,
  pulseCount: null,
  thicknessMode: PersistentHighlightThicknessModes.Thick,
};

// -- Normalization -----------------------------------------------------------

/** Unity's alias folding: trim, lowercase, strip `_`, `-` and spaces. */
function fold(value: string): string {
  return value.trim().toLowerCase().replace(/[-_ ]/g, "");
}

export function normalizeHighlightSemantic(value: unknown): PersistentHighlightSemantic {
  if (typeof value !== "string" || !value.trim()) {
    return PersistentHighlightSemantics.Attention;
  }
  switch (fold(value)) {
    case "attention":
    case "focus":
    case "focused":
    case "current":
    case "currentfocus":
    case "yellow":
      return PersistentHighlightSemantics.Attention;
    case "correct":
    case "completed":
    case "complete":
    case "success":
    case "green":
      return PersistentHighlightSemantics.Correct;
    case "hazard":
    case "error":
    case "danger":
    case "warning":
    case "red":
      return PersistentHighlightSemantics.Hazard;
    default:
      return PersistentHighlightSemantics.Attention;
  }
}

export function normalizeHighlightPulseMode(value: unknown): PersistentHighlightPulseMode {
  if (typeof value !== "string" || !value.trim()) {
    return PersistentHighlightPulseModes.Continuous;
  }
  switch (fold(value)) {
    case "single":
    case "singlesoft":
    case "soft":
    case "once":
      return PersistentHighlightPulseModes.SingleSoft;
    case "continuous":
    case "loop":
    case "looping":
    case "infinite":
    case "infinitely":
    case "forever":
    case "pulse":
    case "pulsing":
      return PersistentHighlightPulseModes.Continuous;
    case "none":
    case "off":
    case "steady":
    case "static":
      return PersistentHighlightPulseModes.None;
    default:
      return PersistentHighlightPulseModes.Continuous;
  }
}

export function normalizeHighlightThicknessMode(
  value: unknown,
): PersistentHighlightThicknessMode {
  if (typeof value !== "string" || !value.trim()) {
    return PersistentHighlightThicknessModes.Thick;
  }
  switch (fold(value)) {
    case "medium":
    case "mid":
    case "moderate":
      return PersistentHighlightThicknessModes.Medium;
    case "thick":
    case "heavy":
    case "heavier":
    case "bold":
    case "strong":
    case "default":
    case "normal":
    case "regular":
    case "standard":
    case "max":
    case "maximum":
      return PersistentHighlightThicknessModes.Thick;
    case "thin":
    case "thinner":
    case "subtle":
    case "light":
    case "slim":
    case "narrow":
      return PersistentHighlightThicknessModes.Thin;
    default:
      return PersistentHighlightThicknessModes.Thick;
  }
}

/** Unity `NormalizePulseCount`: anything `<= 0` (or absent) means infinite. */
export function normalizeHighlightPulseCount(value: unknown): number | null {
  const count = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(count)) return null;
  const whole = Math.trunc(count);
  return whole > 0 ? whole : null;
}

export function normalizeHighlightColorHex(value: unknown): string | null {
  if (typeof value !== "string" || !value.trim()) return null;
  const parsed = tryParseHtmlColor(value) ?? tryParseHtmlColor(`#${value.trim()}`);
  return parsed ? toHtmlStringRgb(parsed) : null;
}

/** Unity `NormalizeInPlace` over a partial/untrusted shape. */
export function normalizeHighlight(
  value: Partial<PersistentHighlightData>,
): PersistentHighlightData {
  return {
    enabled: value.enabled !== false,
    semantic: normalizeHighlightSemantic(value.semantic),
    colorHex: normalizeHighlightColorHex(value.colorHex),
    pulseMode: normalizeHighlightPulseMode(value.pulseMode),
    pulseCount: normalizeHighlightPulseCount(value.pulseCount),
    thicknessMode: normalizeHighlightThicknessMode(value.thicknessMode),
  };
}

// -- Serialization -----------------------------------------------------------

/** Unity `PersistentHighlightData.Deserialize` — null for blank/invalid/disabled. */
export function parsePersistentHighlightJson(
  json: string | null | undefined,
): PersistentHighlightData | null {
  if (!json || !json.trim()) return null;
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return null;
  }
  if (!raw || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  // Unity reads `enabled` off a `[SerializeField] bool` that defaults to true,
  // so a payload that omits it is enabled — but an explicit `false` is not.
  if (record.enabled === false) return null;
  return normalizeHighlight(record as Partial<PersistentHighlightData>);
}

/**
 * Unity `PersistentHighlightData.Serialize` — null when disabled (the caller
 * clears the attribute). Field order and the `""` / `-1` encodings match
 * JsonUtility's output so a Unity peer reads web-written rows unchanged.
 */
export function serializePersistentHighlight(
  highlight: PersistentHighlightData | null,
): string | null {
  if (!highlight || !highlight.enabled) return null;
  const normalized = normalizeHighlight(highlight);
  return JSON.stringify({
    enabled: true,
    semantic: normalized.semantic,
    colorHex: normalized.colorHex ?? "",
    pulseMode: normalized.pulseMode,
    pulseCount: normalized.pulseCount ?? -1,
    thicknessMode: normalized.thicknessMode,
  });
}

/** Unity `ProjectModel_Entities.PersistentHighlightsEqual`. */
export function persistentHighlightsEqual(
  left: PersistentHighlightData | null,
  right: PersistentHighlightData | null,
): boolean {
  if (!left || !right) return !left && !right;
  return left.enabled === right.enabled
    && left.semantic === right.semantic
    && left.colorHex === right.colorHex
    && left.pulseMode === right.pulseMode
    && left.pulseCount === right.pulseCount
    && left.thicknessMode === right.thicknessMode;
}

// -- Derived properties ------------------------------------------------------

export function usesCustomHighlightColor(highlight: PersistentHighlightData): boolean {
  return !!highlight.colorHex;
}

export function shouldPlayHighlightEntryPulse(highlight: PersistentHighlightData): boolean {
  return highlight.pulseMode === PersistentHighlightPulseModes.SingleSoft;
}

export function shouldPulseHighlightContinuously(highlight: PersistentHighlightData): boolean {
  return highlight.pulseMode === PersistentHighlightPulseModes.Continuous;
}

/** Unity `GetIntensityMultiplier` — hazard reads hotter than the rest. */
export function highlightIntensityMultiplier(highlight: PersistentHighlightData): number {
  return highlight.semantic === PersistentHighlightSemantics.Hazard ? 1.5 : 1;
}

function steadyThicknessBias(highlight: PersistentHighlightData): number {
  switch (highlight.thicknessMode) {
    case PersistentHighlightThicknessModes.Medium:
      return 0.45;
    case PersistentHighlightThicknessModes.Thick:
      return 1;
    default:
      return 0;
  }
}

function peakThicknessBias(highlight: PersistentHighlightData): number {
  switch (highlight.thicknessMode) {
    case PersistentHighlightThicknessModes.Medium:
      return 0.78;
    case PersistentHighlightThicknessModes.Thick:
      return 1;
    default:
      return 0.55;
  }
}

/** Unity `ResolveColor` — custom hex wins, else the semantic `Color32`. */
export function resolveHighlightColor(highlight: PersistentHighlightData): Rgb {
  if (highlight.colorHex) {
    const parsed = tryParseHtmlColor(highlight.colorHex);
    if (parsed) return parsed;
  }
  switch (highlight.semantic) {
    case PersistentHighlightSemantics.Correct:
      return [76 / 255, 175 / 255, 80 / 255];
    case PersistentHighlightSemantics.Hazard:
      return [1, 0, 0];
    default:
      return [1, 214 / 255, 10 / 255];
  }
}

// -- AI scene-context labels -------------------------------------------------

/** Unity `GetContextColorLabel`. */
export function highlightContextColorLabel(highlight: PersistentHighlightData): string {
  if (usesCustomHighlightColor(highlight)) {
    return toHtmlStringRgb(resolveHighlightColor(highlight));
  }
  switch (highlight.semantic) {
    case PersistentHighlightSemantics.Correct:
      return "green";
    case PersistentHighlightSemantics.Hazard:
      return "red";
    default:
      return "yellow";
  }
}

/** Unity `GetContextPulseLabel`. */
export function highlightContextPulseLabel(highlight: PersistentHighlightData): string {
  if (highlight.pulseMode === PersistentHighlightPulseModes.None) return "static";
  if (highlight.pulseMode === PersistentHighlightPulseModes.Continuous) {
    if (highlight.pulseCount === null) return "pulsing";
    if (highlight.pulseCount === 1) return "pulse once";
    if (highlight.pulseCount === 2) return "pulse twice";
    return `pulse ${highlight.pulseCount} times`;
  }
  return "pulse once";
}

/** Unity `GetContextThicknessLabel`. */
export function highlightContextThicknessLabel(highlight: PersistentHighlightData): string {
  return highlight.thicknessMode;
}

// -- Visual state ------------------------------------------------------------

/** Unity `PersistentEntityHighlightModelBase.HighlightVisualState`. */
export interface HighlightVisualState {
  readonly color: Rgb;
  readonly intensity: number;
  readonly thickness: number;
  /**
   * True while a pulse owns the thickness, so a trough of 0 must read as "no
   * outline at all" rather than the steady floor.
   */
  readonly allowFadeToOff: boolean;
}

export function isInfiniteHighlightPulseActive(highlight: PersistentHighlightData): boolean {
  return shouldPulseHighlightContinuously(highlight) && highlight.pulseCount === null;
}

function finitePulseDurationSeconds(highlight: PersistentHighlightData): number | null {
  if (!shouldPulseHighlightContinuously(highlight) || highlight.pulseCount === null) {
    return null;
  }
  return highlight.pulseCount / HIGHLIGHT_PULSE_CYCLES_PER_SECOND;
}

export function isFiniteHighlightPulseActive(
  highlight: PersistentHighlightData,
  elapsedSeconds: number,
): boolean {
  const duration = finitePulseDurationSeconds(highlight);
  return duration !== null && elapsedSeconds < duration;
}

/** Unity `HasFinitePulseAnimationExpired` — the auto-clear trigger. */
export function hasFiniteHighlightPulseExpired(
  highlight: PersistentHighlightData,
  elapsedSeconds: number,
): boolean {
  const duration = finitePulseDurationSeconds(highlight);
  return duration !== null && elapsedSeconds >= duration;
}

/** Unity `RequiresAnimationUpdate`. */
export function requiresHighlightAnimation(
  highlight: PersistentHighlightData,
  elapsedSeconds: number,
  playEntryPulse: boolean,
): boolean {
  if (isInfiniteHighlightPulseActive(highlight)) return true;
  if (isFiniteHighlightPulseActive(highlight, elapsedSeconds)) return true;
  return playEntryPulse && elapsedSeconds < HIGHLIGHT_ENTRY_PULSE_DURATION_SECONDS;
}

/** Unity `PersistentEntityHighlightModelBase.ComputeVisualState`. */
export function computeHighlightVisualState(
  highlight: PersistentHighlightData,
  elapsedSeconds: number,
  playEntryPulse: boolean,
): HighlightVisualState {
  const steadyIntensity = clamp01(K_STEADY_INTENSITY);
  let steadyThickness = clamp01(K_STEADY_THICKNESS);
  let peakThickness = clamp01(Math.max(steadyThickness, K_PEAK_THICKNESS));

  const steadyBias = steadyThicknessBias(highlight);
  const peakBias = peakThicknessBias(highlight);
  if (steadyBias > 0) steadyThickness = lerp(steadyThickness, 1, steadyBias);
  if (peakBias > 0) {
    peakThickness = Math.max(steadyThickness, lerp(peakThickness, 1, peakBias));
  }

  let thickness = steadyThickness;
  let allowFadeToOff = false;

  if (isInfiniteHighlightPulseActive(highlight)) {
    allowFadeToOff = true;
    const wave = smoothStep01(
      0.5 + 0.5 * Math.cos(elapsedSeconds * Math.PI * 2 * HIGHLIGHT_PULSE_CYCLES_PER_SECOND),
    );
    thickness = lerp(0, peakThickness, wave);
  } else if (isFiniteHighlightPulseActive(highlight, elapsedSeconds)) {
    allowFadeToOff = true;
    const pulse = Math.sin(elapsedSeconds * Math.PI * HIGHLIGHT_PULSE_CYCLES_PER_SECOND) ** 2;
    thickness = lerp(0, peakThickness, pulse);
  } else if (playEntryPulse) {
    const pulseTime = clamp01(elapsedSeconds / HIGHLIGHT_ENTRY_PULSE_DURATION_SECONDS);
    if (pulseTime < 1) {
      const entryPulse = Math.sin(pulseTime * Math.PI) ** 2;
      thickness = lerp(steadyThickness, peakThickness, entryPulse);
    }
  }

  return {
    color: resolveHighlightColor(highlight),
    intensity: clamp01(steadyIntensity * highlightIntensityMultiplier(highlight)),
    thickness,
    allowFadeToOff,
  };
}

// -- Color parsing (Unity `ColorUtility`) ------------------------------------

/**
 * Unity's named-color table (`ColorUtility.TryParseHtmlString` accepts these
 * case-insensitively alongside `#RGB` / `#RGBA` / `#RRGGBB` / `#RRGGBBAA`).
 * Kept because Unity accepts `colorHex: "red"` and the normalizer must fold it
 * to `#FF0000` on both clients.
 */
const K_NAMED_COLORS: Readonly<Record<string, string>> = {
  red: "ff0000",
  cyan: "00ffff",
  blue: "0000ff",
  darkblue: "0000a0",
  lightblue: "add8e6",
  purple: "800080",
  yellow: "ffff00",
  lime: "00ff00",
  fuchsia: "ff00ff",
  white: "ffffff",
  silver: "c0c0c0",
  grey: "808080",
  gray: "808080",
  black: "000000",
  orange: "ffa500",
  brown: "a52a2a",
  maroon: "800000",
  green: "008000",
  olive: "808000",
  navy: "000080",
  teal: "008080",
  aqua: "00ffff",
  magenta: "ff00ff",
};

/** Unity `ColorUtility.TryParseHtmlString`; alpha is parsed and then dropped. */
function tryParseHtmlColor(value: string): Rgb | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const named = K_NAMED_COLORS[trimmed.toLowerCase()];
  if (named) return hexTripletToRgb(named);

  if (!trimmed.startsWith("#")) return null;
  const digits = trimmed.slice(1).toLowerCase();
  if (!/^[0-9a-f]+$/.test(digits)) return null;
  if (digits.length === 3 || digits.length === 4) {
    return hexTripletToRgb(
      digits
        .slice(0, 3)
        .split("")
        .map((c) => c + c)
        .join(""),
    );
  }
  if (digits.length === 6 || digits.length === 8) return hexTripletToRgb(digits.slice(0, 6));
  return null;
}

function hexTripletToRgb(digits: string): Rgb {
  return [
    parseInt(digits.slice(0, 2), 16) / 255,
    parseInt(digits.slice(2, 4), 16) / 255,
    parseInt(digits.slice(4, 6), 16) / 255,
  ];
}

/** Unity `ColorUtility.ToHtmlStringRGB` — uppercase, `#`-prefixed, no alpha. */
export function toHtmlStringRgb(color: Rgb): string {
  const byte = (value: number) =>
    Math.min(255, Math.max(0, Math.round(value * 255)))
      .toString(16)
      .padStart(2, "0")
      .toUpperCase();
  return `#${byte(color[0])}${byte(color[1])}${byte(color[2])}`;
}

// -- Math --------------------------------------------------------------------

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

function lerp(from: number, to: number, t: number): number {
  return from + (to - from) * clamp01(t);
}

/** Unity `Mathf.SmoothStep(0, 1, t)`. */
function smoothStep01(t: number): number {
  const x = clamp01(t);
  return x * x * (3 - 2 * x);
}
